import { line, Size, t, type FrozenDealTeam, formatPercent } from '@bms2/shared';
import { DetailList, DetailRow } from '../../components/composites/DetailList';
import { Pill } from '../../components/controls';
import { useDateFormat } from '../../shell/ViewerZone';
import { whenText } from '../../shell/viewerDates';
import styles from './DocumentPage.module.css';

/**
 * 🔒 The Deal team as it stood when this document was created (piece 22;
 * decision 0765, T129): each member with their role, their share of the
 * commission and the primary mark, read from the team frozen on the document
 * rather than from the Deal today. A team the migration filled after the
 * document was created says so, and on which day.
 *
 * 🔨 A document with no Deal draws no panel at all, rather than an empty one:
 * a heading with nothing under it reads as a fault.
 */
export function DealTeamPanel({ team, timeZone = 'UTC', bare = false }: { team: FrozenDealTeam | null | undefined; timeZone?: string; bare?: boolean }) {
  const dateFormat = useDateFormat();
  // 🔨 Inside a tile, which carries its own title, a job with no Deal says so rather than leaving the tile blank.
  if (!team) return bare ? <p className={styles.muted} data-document-deal-team="none">{t('dealTeamPanel.noDeal')}</p> : null;
  const when = whenText(team.frozenAt, timeZone, dateFormat);
  return (
    <section className={bare ? undefined : styles.block} data-document-deal-team={team.members.length} data-deal-team-filled={team.filledAfterTheFact ? 'true' : 'false'}>
      {bare ? null : <h2 className={styles.heading}>{t('dealTeamPanel.title')}</h2>}
      <p className={styles.muted}>{line(team.filledAfterTheFact ? t('dealTeamPanel.filled') : t('dealTeamPanel.frozen'), when)}</p>
      {team.members.length === 0 ? <p className={styles.muted}>{t('dealTeamPanel.none')}</p> : null}
      <DetailList look="separator">
        {team.members.map((member) => (
          <DetailRow
            key={member.employmentId || member.personName}
            data-deal-team-member={member.employmentId}
            title={member.personName}
            detail={member.roleName}
            columns={[{
              value: member.commissionSharePercent === null ? t('dealTeamPanel.noShare') : `${formatPercent(member.commissionSharePercent, { maxDecimals: 4 })} ${t('dealTeamPanel.share')}`,
              'data-deal-team-share': member.commissionSharePercent ?? '',
            }]}
            badges={member.isPrimary ? <Pill text={t('dealTeamPanel.primary')} foreground="var(--status-good-text)" background="var(--status-good-bg)" size={Size.Small} /> : null}
          />
        ))}
      </DetailList>
    </section>
  );
}
